<script setup lang="ts">
import { useForm, useField } from 'vee-validate'
import { string } from 'yup'

const props = defineProps<{ resetToken: string; login?: string }>()

const {
  values: formValues,
  errors,
  meta,
  validate,
} = useForm({
  initialValues: {
    email: props.login ?? '',
    password: '',
    confirmPassword: '',
  },
})

const { t } = useI18n()
const success = ref(false)
const formSubmitted = ref(false)
const isLoading = ref(false)
const autoLoggedIn = ref(false)
const uiStore = useUIStore()
const { _route } = useNuxtApp()
const activation = _route.query.activation
const attempt = ref(0)

const emailValidate = string().email(t('validation_messages.email')).required(t('validation_messages.requiredEmail'))

const passwordValidate = string()
  .matches(REGEX_PASSWORD, t('validation_messages.password'))
  .required(t('validation_messages.requiredPassword'))

const confirmPasswordValidate = (value: string) => {
  if (!value) {
    return t('validation_messages.requiredConfirmPassword')
  }
  return value === formValues.password ? true : t('validation_messages.confirmPassword')
}

const { value: email, validate: validateEmail } = useField('email', emailValidate, {
  validateOnValueUpdate: false,
})
const { value: password, validate: validatePassword } = useField('password', passwordValidate, {
  validateOnValueUpdate: false,
})
const { value: confirmPassword, validate: validateConfirmPassword } = useField(
  'confirmPassword',
  confirmPasswordValidate,
  {
    validateOnValueUpdate: false,
  },
)

const scrollToInvalidField = (selector?: string) => scrollToFieldOrLastInvalid('#resetPassword', errors.value, selector)
const handleBlurValidation = (validator: () => Promise<{ valid: boolean }>, selector: string) =>
  validateOnBlur(validator, selector, '#resetPassword', errors.value)

const checkConfirmPassword = () => {
  if (confirmPassword.value) {
    validateConfirmPassword()
  }
}

const handleResetPassword = async () => {
  attempt.value++
  try {
    const { valid } = await validate()
    if (!valid) {
      scrollToInvalidField()
      return
    }

    isLoading.value = true
    formSubmitted.value = true

    if (activation === 'true' && formValues.email) {
      const { activateCustomer, error: activateError } = useActivateCustomer()
      const activateCustomerSuccess = await activateCustomer({
        resetToken: props.resetToken,
        login: formValues.email,
        newPassword: formValues.password,
      })

      if (!activateCustomerSuccess) {
        isLoading.value = false
        console.error(activateError.value?.message)
        return
      }
    } else {
      const { error, resetPassword } = useResetPassword()
      const resetPasswordSuccess = await resetPassword({
        token: props.resetToken,
        email: formValues.email,
        newPassword: formValues.password,
      })

      if (!resetPasswordSuccess) {
        isLoading.value = false
        console.error(error.value?.message)
        return
      }
    }

    const { signIn } = useLogin()
    autoLoggedIn.value = await signIn(formValues.email, formValues.password)

    success.value = true
  } catch (err) {
    console.error('An unexpected error occurred during password reset:', err)
  } finally {
    isLoading.value = false
  }
}

const openLoginModal = () => {
  uiStore.isShowResetPasswordModal = false
  uiStore.isShowLoginModal = true
}

const openForgotPasswordModal = () => {
  uiStore.isShowResetPasswordModal = false
  uiStore.isShowForgotPasswordModal = true
}
</script>

<template>
  <section class="reset-password">
    <template v-if="isLoading || !formSubmitted">
      <h4 class="reset-password__heading">{{ t('my_account.new_password.title') }}</h4>

      <form class="reset-password__form" @submit.prevent="handleResetPassword" id="resetPassword" novalidate>
        <Input
          v-model="email"
          type="email"
          :error="errors.email"
          :label="t('my_account.register.inputs.email.label')"
          name="email"
          input-test-id="reset-password-email"
          required
          :attempt="attempt"
          @blur="handleBlurValidation(validateEmail, 'email')"
        />
        <Input
          v-model="password"
          type="password"
          @input="checkConfirmPassword"
          @blur="handleBlurValidation(validatePassword, 'password')"
          :error="errors.password"
          :label="t('my_account.register.inputs.password.label')"
          :hint="t('my_account.register.password_hint_text')"
          name="password"
          input-test-id="reset-password-new"
          required
          :attempt="attempt"
        />

        <Input
          v-model="confirmPassword"
          type="password"
          @blur="handleBlurValidation(validateConfirmPassword, 'confirmPassword')"
          :error="errors.confirmPassword"
          name="confirmPassword"
          input-test-id="reset-password-confirm"
          :label="t('my_account.register.inputs.confirm_password.label')"
          required
          :attempt="attempt"
        />
        <Button
          variant="primary"
          type="submit"
          class="reset-password-form__submit-button"
          data-testid="reset-password-submit"
          :disabled="isLoading"
        >
          {{ t('my_account.new_password.submit_button_text') }}
        </Button>
      </form>
    </template>

    <!-- Show success/failure message -->
    <template v-else>
      <div v-if="success" class="reset-password__success-message" data-testid="reset-password-success">
        <AlertBar variant="success">
          <span>{{ t('my_account.new_password.success_title') }}</span>
        </AlertBar>
        <p class="new-password__sub-title">
          {{ t('my_account.new_password.success_sub_title') }}
          <template v-if="!autoLoggedIn">
            <button class="button-text" type="button" @click="openLoginModal">
              {{ t('my_account.login_page.inputs.login.label') }}</button
            >.
          </template>
        </p>
      </div>
      <div v-else class="reset-password__failure-message" data-testid="reset-password-failure">
        <AlertBar variant="error">
          <span>{{ t('my_account.new_password.failure_title') }}</span>
        </AlertBar>
        <p class="new-password__sub-title">
          {{ t('my_account.new_password.failure_sub_title') }}
          <button class="button-text" type="button" @click="openForgotPasswordModal">
            {{ t('my_account.new_password.failure_sub_title_retry') }}</button
          >.
        </p>
      </div>
    </template>

    <LoadingSpinner v-if="isLoading" />
  </section>
</template>

<style lang="postcss">
.reset-password {
  position: relative;
  /* required for the spinner */

  .reset-password__heading {
    font-weight: bold;
  }

  .reset-password__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
    margin-top: var(--space-50);

    .reset-password-form__submit-button {
      align-self: flex-end;
    }
  }

  .error {
    color: var(--status-error);
    margin-top: var(--space-50);
  }

  .new-password__sub-title {
    margin-top: var(--space-50);
  }
}
</style>
